import { ClickAwayListener, Popper } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import { memo, useState } from "react";
import { DateRangePicker } from "./date-picker/date-picker-menu";

export interface RedoDateRangePickerDropdownProps {
  "data-dd-action-name"?: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  setDateRange: (
    dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  allowNoDateOption?: boolean;
  style?: React.CSSProperties;
  popperModifiers?: any[];
  placement?: "bottom-start" | "top-start";
}

export const RedoDateRangePickerDropdown = memo(
  function RedoDateRangePickerDropdown({
    anchorEl,
    open,
    onClose,
    dateRange,
    setDateRange,
    minDate,
    maxDate,
    allowNoDateOption = false,
    style,
    popperModifiers,
    placement = "bottom-start",
    "data-dd-action-name": actionName,
  }: RedoDateRangePickerDropdownProps) {
    const [localStartDate, setLocalStartDate] =
      useState<Temporal.PlainDate | null>(dateRange ? dateRange[0] : null);

    const [localEndDate, setLocalEndDate] = useState<Temporal.PlainDate | null>(
      dateRange ? dateRange[1] : null,
    );

    const [prevOpen, setPrevOpen] = useState(open);

    const handleSetCustomDate = useHandler(
      (start: Temporal.PlainDate | null, end: Temporal.PlainDate | null) => {
        setLocalStartDate(start);
        setLocalEndDate(end);
      },
    );

    // Reset the in-progress selection to the applied range each time the dropdown
    // opens, so reopening reflects what's applied rather than stale, un-applied
    // edits from a previous cancel. Adjusting state during render (per "You Might
    // Not Need an Effect") avoids a prop-syncing effect.
    if (open !== prevOpen) {
      setPrevOpen(open);
      if (open) {
        setLocalStartDate(dateRange ? dateRange[0] : null);
        setLocalEndDate(dateRange ? dateRange[1] : null);
      }
    }

    const handleApply = useHandler(() => {
      if (localStartDate && localEndDate) {
        const newArray: [Temporal.PlainDate, Temporal.PlainDate] = [
          localStartDate,
          localEndDate,
        ];

        setDateRange(newArray);
      } else {
        setDateRange(undefined);
      }
      onClose();
    });

    const defaultModifiers = [
      { name: "offset", options: { offset: [0, 8] } },
      { name: "preventOverflow", options: { padding: 8 } },
      { name: "flip", options: { altBoundary: true } },
    ];

    return (
      <ClickAwayListener onClickAway={onClose}>
        <Popper
          anchorEl={anchorEl}
          data-arbiter="RedoDateRangePickerDropdown"
          data-dd-action-name={actionName}
          modifiers={popperModifiers ?? defaultModifiers}
          open={open}
          placement={placement}
          style={style}
        >
          <DateRangePicker
            allowNoDateOption={allowNoDateOption}
            endDate={localEndDate}
            maxDate={maxDate}
            minDate={minDate}
            onApply={handleApply}
            onCancel={onClose}
            onClear={
              allowNoDateOption
                ? () => {
                    handleSetCustomDate(null, null);
                  }
                : undefined
            }
            onRangeChange={handleSetCustomDate}
            startDate={localStartDate}
            variant="range"
          />
        </Popper>
      </ClickAwayListener>
    );
  },
);
